import { supabase } from '../supabaseClient';
import { whatsappDigitsValido } from './authIdentidad';
import { upsertJugadorPerfilPorSesion } from './upsertJugadorPerfil';

export const JUGADOR_PERFIL_LS_KEY = 'jugadorPerfil';

/** Clave legacy / alias; limpiar junto con {@link JUGADOR_PERFIL_LS_KEY}. */
export const JUGADOR_PERFIL_LS_KEY_ALT = 'jugadores_perfil';

export const PERFIL_CHANGE_EVENT = 'padbol-jugador-perfil';

/** Borra perfil de jugador en localStorage (evita identidad fantasma sin sesión). */
export function clearJugadorPerfilLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(JUGADOR_PERFIL_LS_KEY);
    localStorage.removeItem(JUGADOR_PERFIL_LS_KEY_ALT);
  } catch {
    /* ignore */
  }
}

export function readJugadorPerfil() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(JUGADOR_PERFIL_LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
}

/** Nombre visible: nombre + apellido (solo el objeto pasado; no leer localStorage). */
export function nombreCompletoJugadorPerfil(p) {
  const x = p && typeof p === 'object' ? p : null;
  if (!x) return '';
  const n = String(x.nombre ?? '').trim();
  const a = String(x.apellido ?? '').trim();
  return [n, a].filter(Boolean).join(' ').trim();
}

/** Nombre visible en torneos / rankings / fixture: nombre + apellido; sin usar alias como texto principal. */
export function nombreListadoTorneoRanking(p) {
  const full = nombreCompletoJugadorPerfil(p);
  if (full) return full;
  const n = String(p?.nombre || '').trim();
  if (n) return n;
  return 'Jugador';
}

function nombreApellidoEfectivos(p) {
  if (!p || typeof p !== 'object') return { nombre: '', apellido: '' };
  let nombre = String(p.nombre ?? '').trim();
  let apellido = String(p.apellido ?? '').trim();
  if (!apellido && nombre.includes(' ')) {
    const parts = nombre.split(/\s+/).filter(Boolean);
    nombre = parts[0] || '';
    apellido = parts.slice(1).join(' ') || '';
  }
  return { nombre, apellido };
}

/** WhatsApp obligatorio (notificaciones); formato internacional o legacy. */
export function whatsappPerfilValido(perfil) {
  const p = perfil ?? readJugadorPerfil();
  if (!p || typeof p !== 'object') return false;
  const wa = String(p.whatsapp ?? '').trim();
  return whatsappDigitsValido(wa);
}

/** Perfil mínimo torneo: nombre, apellido, género, categoría, lateralidad y WhatsApp válido. */
export function isPerfilTorneoCompleto(perfil) {
  const p = perfil ?? readJugadorPerfil();
  if (!p || typeof p !== 'object') return false;
  const nombre = String(p.nombre ?? '').trim();
  const apellido = String(p.apellido ?? '').trim();
  const categoria = String(p.categoria ?? p.nivel ?? '').trim();
  const lateralidad = String(p.lateralidad ?? '').trim();
  const genero = String(p.genero ?? '').trim();
  if (!whatsappPerfilValido(p)) return false;
  if (!nombre || !apellido || !categoria || !lateralidad || !genero) return false;
  return true;
}

/**
 * Ficha “suficiente” para no mostrar aviso pendiente en torneo: nombre, apellido y género
 * (alineado con `jugadores_perfil` vía LS). No exige WhatsApp, categoría, lateralidad, foto ni alias.
 */
export function isFichaJugadorBasicaCompleta(perfil) {
  const p = perfil ?? readJugadorPerfil();
  if (!p || typeof p !== 'object') return false;
  const { nombre, apellido } = nombreApellidoEfectivos(p);
  const genero = String(p.genero ?? '').trim();
  return Boolean(nombre && apellido && genero);
}

/** Identidad de torneo sin cuenta Supabase */
export function tieneRegistroTorneo() {
  return isPerfilTorneoCompleto();
}

export function persistJugadorPerfil(partial) {
  if (typeof window === 'undefined') return;
  const prev = readJugadorPerfil() || {};
  const next = { ...prev };
  Object.entries(partial || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) next[k] = v;
  });
  try {
    localStorage.setItem(JUGADOR_PERFIL_LS_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(PERFIL_CHANGE_EVENT));
  } catch {
    /* ignore */
  }
}

/**
 * Garantiza fila en jugadores_perfil para el email (nombre desde perfil local, sin usar email como nombre).
 * Usa upsert por email. Si falta nombre completo en LS, devuelve error.
 */
export async function ensureJugadorPerfilRowForEmail(email) {
  const em = String(email || '').trim();
  if (!em) return { error: null };
  const jp = readJugadorPerfil();
  const full = nombreCompletoJugadorPerfil(jp).trim();
  if (!full) {
    return { error: new Error('Completa nombre y apellido en tu perfil antes de crear un equipo.') };
  }
  const wa = String(jp?.whatsapp || '').trim();
  if (!whatsappPerfilValido(jp)) {
    return { error: new Error('Completa un WhatsApp válido en tu perfil.') };
  }
  const row = {
    nombre: full,
    whatsapp: wa,
    nivel: String(jp?.categoria || jp?.nivel || 'Principiante').trim() || 'Principiante',
    lateralidad: 'Diestro',
    pendiente_validacion: true,
    es_federado: false,
  };
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData?.session?.user?.id ?? null;
  if (uid) {
    const { error } = await upsertJugadorPerfilPorSesion({ userId: uid, email: em, row });
    return { error };
  }
  const { error } = await supabase.from('jugadores_perfil').upsert(
    { email: em, ...row },
    { onConflict: 'email' }
  );
  return { error };
}

export async function refreshJugadorPerfilFromSupabase(email, { profileRow, isCurrent = () => true } = {}) {
  const em = String(email || '').trim();
  if (!em) return;
  try {
    const [{ data, error }, { data: cli }] = await Promise.all([
      profileRow !== undefined
        ? Promise.resolve({ data: profileRow, error: null })
        : supabase.from('jugadores_perfil').select('nombre, apellido, nivel, whatsapp, foto_url, genero, lateralidad').eq('email', em).maybeSingle(),
      String(profileRow?.whatsapp || '').trim()
        ? Promise.resolve({ data: null })
        : supabase.from('clientes').select('whatsapp').eq('email', em).maybeSingle(),
    ]);
    if (!isCurrent()) return;
    const waCli = String(cli?.whatsapp || '').trim();
    const waJp = String(data?.whatsapp || '').trim();
    const wa = waJp || waCli;
    if (error || !data) {
      if (waCli) {
        persistJugadorPerfil({ whatsapp: waCli, email: em });
      }
      return;
    }
    const { nombre: n0, apellido: a0 } = nombreApellidoEfectivos({
      nombre: data.nombre,
      apellido: data.apellido ?? '',
    });
    const nombre = n0;
    const apellido = a0;
    const categoria = String(data.nivel || '').trim();
    const fotoUrl = String(data?.foto_url || '').trim();
    const genero = String(data?.genero ?? '').trim();
    const lateralidad = String(data?.lateralidad ?? '').trim();
    persistJugadorPerfil({
      ...(nombre ? { nombre } : {}),
      ...(apellido ? { apellido } : {}),
      ...(categoria ? { categoria } : {}),
      ...(wa ? { whatsapp: wa } : {}),
      ...(fotoUrl ? { foto_url: fotoUrl } : {}),
      ...(genero ? { genero } : {}),
      ...(lateralidad ? { lateralidad } : {}),
      email: em,
    });
  } catch {
    /* ignore */
  }
}

/** Alias para UI: siempre con `@` delante, sin duplicar arrobas. */
export function formatAliasConArroba(raw) {
  let a = String(raw || '').trim();
  if (!a) return '';
  a = a.replace(/^@+/u, '');
  return `@${a}`;
}

/**
 * ¿Mostrar aviso "pendiente de validación" de categoría?
 * Evita strings `"false"` de Postgres que en JS son truthy con `&&`.
 * Si existe `categoria_validada` explícita como validada, no mostrar pendiente.
 */
export function esCategoriaPendienteValidacion(perfil) {
  if (!perfil || typeof perfil !== 'object') return false;
  const cv = perfil.categoria_validada;
  if (cv === true || cv === 1) return false;
  const cvStr = String(cv ?? '').trim().toLowerCase();
  if (cvStr === 'true' || cvStr === '1') return false;

  const raw = perfil.pendiente_validacion;
  if (raw === false || raw === 0 || raw === null) return false;
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === '' || s === 'false' || s === '0' || s === 'f' || s === 'no') return false;
  if (raw === true || raw === 1) return true;
  return s === 'true' || s === '1' || s === 't' || s === 'yes';
}
